import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import moment from 'moment'
// Même trame que Abonnement.js / CompteUtilisateur.js : on charge les 5 locales
// supportées pour que les jours ("lun. 24 août" / "Mon 24 Aug") suivent la
// langue active de l'utilisateur, quel que soit le locale global laissé par
// une autre page.
import 'moment/locale/fr'
import 'moment/locale/de'
import 'moment/locale/it'
import 'moment/locale/es'
import { useTranslation } from 'react-i18next'
import { makeGetRequest, makePostRequest } from 'utils/api'
import urls from 'utils/urls'

// LAP — Génération de menu avancée. Ce formulaire pilote l'endpoint
// POST /api/Personnal/genrate-menu/ : période, repas, exclusions, nombre de
// personnes, critères de recette et options (variété, restes, repas complets).

const MEALS = ['Matin', 'Midi', 'Après-midi', 'Soir']
const SEASONS = ['Hiver', 'Printemps', 'Été', 'Automne', 'Toutes']

// Champs sombres pour coller au thème de la page Menu.
const fieldSx = {
  '& .MuiInputBase-root': { color: '#fff' },
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#7cc6b2' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7cc6b2' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '& input[type=date]::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
}

const switchSx = {
  '& .MuiFormControlLabel-label': { color: 'rgba(255,255,255,0.88)', fontSize: 14 },
}

export default function GenerateMenuForm({ weekStartDate, onClose, onGenerated }) {
  const { t, i18n: i18nInstance } = useTranslation('layout')
  const [open, setOpen] = useState(true)

  // Langue active (fr par défaut) — utilisée pour formater les libellés de
  // jours sans dépendre du locale global de moment.
  const rawLang = (i18nInstance.language || 'fr').split('-')[0]
  const lang = ['fr', 'en', 'de', 'it', 'es'].includes(rawLang) ? rawLang : 'fr'

  // --- Période -------------------------------------------------------------
  const defaultStart = weekStartDate || moment().format('YYYY-MM-DD')
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(
    moment(defaultStart, 'YYYY-MM-DD').add(6, 'days').format('YYYY-MM-DD'),
  )

  // --- Repas, exclusions, personnes ---------------------------------------
  const [meals, setMeals] = useState(['Matin', 'Midi', 'Soir'])
  const [excludedDates, setExcludedDates] = useState([])
  const [defaultServings, setDefaultServings] = useState('')
  const [overrides, setOverrides] = useState([])

  // --- Critères de recette -------------------------------------------------
  const [filters, setFilters] = useState({ cuisine: [], plat: [], category: [] })
  const [cuisine, setCuisine] = useState([])
  const [dishType, setDishType] = useState([])
  const [category, setCategory] = useState([])
  const [season, setSeason] = useState('auto')

  // --- Temps de préparation ------------------------------------------------
  const [maxPrep, setMaxPrep] = useState('')
  const [weekendDifferent, setWeekendDifferent] = useState(false)
  const [maxPrepWeekend, setMaxPrepWeekend] = useState('')

  // --- Options -------------------------------------------------------------
  const [noRepeat, setNoRepeat] = useState(false)
  const [multiCourse, setMultiCourse] = useState(false)
  const [courseMeals, setCourseMeals] = useState(['Soir'])
  const [leftovers, setLeftovers] = useState(false)
  const [mode, setMode] = useState('complete')

  // --- État UI -------------------------------------------------------------
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchFilters = async () => {
      const { data, status } = await makeGetRequest(urls.getFilters, {}, true)
      if (status === 200) {
        setFilters({
          cuisine: data.cuisine || [],
          plat: data.plat || [],
          category: data.category || [],
        })
      }
    }
    fetchFilters()
  }, [])

  // Jours sélectionnables pour les exclusions / exceptions : uniquement les
  // jours futurs de la période (le passé ne peut pas être planifié).
  const dayOptions = useMemo(() => {
    const out = []
    const start = moment(startDate, 'YYYY-MM-DD')
    const end = moment(endDate, 'YYYY-MM-DD')
    const today = moment().startOf('day')
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return out
    const cursor = start.clone()
    let guard = 0
    while (cursor.isSameOrBefore(end) && guard < 120) {
      if (cursor.isAfter(today)) {
        out.push({
          value: cursor.format('YYYY-MM-DD'),
          // Locale appliqué sur la copie : les jours suivent la langue de
          // l'utilisateur ("lun. 24 août" / "Mon 24 Aug" / "lun 24 ago").
          label: cursor.clone().locale(lang).format('ddd D MMM'),
        })
      }
      cursor.add(1, 'day')
      guard += 1
    }
    return out
  }, [startDate, endDate, lang])

  const handleClose = () => {
    setOpen(false)
    if (onClose) onClose()
  }

  const toggleMeal = (meal) => {
    setMeals((prev) =>
      prev.includes(meal) ? prev.filter((m) => m !== meal) : [...prev, meal],
    )
  }

  const toggleCourseMeal = (meal) => {
    setCourseMeals((prev) =>
      prev.includes(meal) ? prev.filter((m) => m !== meal) : [...prev, meal],
    )
  }

  const addOverride = () => {
    setOverrides((prev) => [...prev, { date: '', servings: '' }])
  }

  const updateOverride = (index, key, value) => {
    setOverrides((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    )
  }

  const removeOverride = (index) => {
    setOverrides((prev) => prev.filter((_, i) => i !== index))
  }

  const handleGenerate = async () => {
    setError('')
    if (!meals.length) {
      setError(t('planMeal.generate.noMeals'))
      return
    }
    if (moment(endDate).isBefore(moment(startDate))) {
      setError(t('planMeal.generate.badRange'))
      return
    }

    const payload = {
      start_date: startDate,
      end_date: endDate,
      meals,
      excluded_dates: excludedDates.map((d) => d.value),
      default_servings: defaultServings ? Number(defaultServings) : 0,
      servings_overrides: overrides
        .filter((o) => o.date && o.servings)
        .map((o) => ({ date: o.date, servings: Number(o.servings) })),
      // get-filters agrège les doublons de libellé : `ids` couvre tous les
      // pks d'un même nom pour ne rater aucune recette au filtrage.
      cuisine_ids: cuisine.flatMap((c) => c.ids || [c.id]),
      dish_type_ids: dishType.flatMap((d) => d.ids || [d.id]),
      category_ids: category.flatMap((c) => c.ids || [c.id]),
      seasons: season === 'auto' ? ['auto'] : [season],
      max_prep_minutes: maxPrep ? Number(maxPrep) : 0,
      max_prep_weekend_minutes:
        weekendDifferent && maxPrepWeekend ? Number(maxPrepWeekend) : 0,
      no_repeat: noRepeat,
      multi_course: multiCourse,
      course_meals: multiCourse ? courseMeals : [],
      leftovers,
      mode,
    }

    setLoading(true)
    const response = await makePostRequest(urls.generateMenu, payload, {}, true)
    setLoading(false)

    if (response && response.status === 200) {
      if (onGenerated) onGenerated(response.data)
      handleClose()
    } else {
      setError(
        (response && response.data) ||
          t('planMeal.generate.genericError'),
      )
    }
  }

  const sectionTitleSx = {
    color: '#7cc6b2',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    mb: 1.2,
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: '#0a1130',
          backgroundImage: 'linear-gradient(180deg, #0d1838 0%, #0a1130 100%)',
          color: '#fff',
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.1)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1}>
            <AutoAwesomeIcon sx={{ color: '#7cc6b2' }} />
            <Typography sx={{ fontWeight: 950, fontSize: 20 }}>
              {t('planMeal.generate.title')}
            </Typography>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mt: 0.5 }}>
          {t('planMeal.generate.subtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        {/* Période ----------------------------------------------------------*/}
        <Typography sx={sectionTitleSx}>{t('planMeal.generate.sectionPeriod')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={t('planMeal.generate.fieldFrom')}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label={t('planMeal.generate.fieldTo')}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate }}
            sx={fieldSx}
          />
        </Stack>

        {/* Repas ------------------------------------------------------------*/}
        <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
          {t('planMeal.generate.sectionMeals')}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {MEALS.map((meal) => (
            <Chip
              key={meal}
              label={meal}
              onClick={() => toggleMeal(meal)}
              variant={meals.includes(meal) ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 800,
                color: '#fff',
                bgcolor: meals.includes(meal) ? 'rgba(124,198,178,0.28)' : 'transparent',
                borderColor: 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </Stack>

        {/* Nombre de personnes ---------------------------------------------*/}
        <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
          {t('planMeal.generate.sectionServings')}
        </Typography>
        <TextField
          size="small"
          type="number"
          label={t('planMeal.generate.fieldDefaultServings')}
          value={defaultServings}
          onChange={(e) => setDefaultServings(e.target.value)}
          inputProps={{ min: 1 }}
          sx={{ ...fieldSx, width: 200 }}
        />
        <Stack gap={1} sx={{ mt: 1.5 }}>
          {overrides.map((row, index) => (
            <Stack key={index} direction="row" gap={1} alignItems="center">
              <FormControl size="small" sx={{ ...fieldSx, flex: 1 }}>
                <InputLabel>{t('planMeal.generate.overrideDate')}</InputLabel>
                <Select
                  label={t('planMeal.generate.overrideDate')}
                  value={row.date}
                  onChange={(e) => updateOverride(index, 'date', e.target.value)}
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#10204a', color: '#fff' } } }}
                >
                  {dayOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label={t('planMeal.generate.overridePeople')}
                value={row.servings}
                onChange={(e) => updateOverride(index, 'servings', e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ ...fieldSx, width: 120 }}
              />
              <IconButton
                onClick={() => removeOverride(index)}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.55)' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            onClick={addOverride}
            startIcon={<AddIcon />}
            sx={{ alignSelf: 'flex-start', color: '#7cc6b2', textTransform: 'none', fontWeight: 800 }}
          >
            {t('planMeal.generate.addOverride')}
          </Button>
        </Stack>

        {/* Jours à exclure --------------------------------------------------*/}
        <Typography sx={{ ...sectionTitleSx, mt: 2 }}>
          {t('planMeal.generate.sectionExclude')}
        </Typography>
        <Autocomplete
          multiple
          options={dayOptions}
          value={excludedDates}
          onChange={(e, value) => setExcludedDates(value)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={t('planMeal.generate.excludeHint')}
              sx={fieldSx}
            />
          )}
          ChipProps={{ sx: { bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' } }}
        />

        {/* Critères de recette ---------------------------------------------*/}
        <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
          {t('planMeal.generate.sectionCriteria')}
        </Typography>
        <Stack gap={1.5}>
          <Autocomplete
            multiple
            options={filters.cuisine}
            value={cuisine}
            onChange={(e, value) => setCuisine(value)}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('planMeal.generate.fieldCuisine')}
                sx={fieldSx}
              />
            )}
            ChipProps={{ sx: { bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' } }}
          />
          <Autocomplete
            multiple
            options={filters.plat}
            value={dishType}
            onChange={(e, value) => setDishType(value)}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('planMeal.generate.fieldDishType')}
                sx={fieldSx}
              />
            )}
            ChipProps={{ sx: { bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' } }}
          />
          <Autocomplete
            multiple
            options={filters.category}
            value={category}
            onChange={(e, value) => setCategory(value)}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('planMeal.generate.fieldCategory')}
                sx={fieldSx}
              />
            )}
            ChipProps={{ sx: { bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' } }}
          />
          <FormControl size="small" sx={fieldSx}>
            <InputLabel>{t('planMeal.generate.fieldSeason')}</InputLabel>
            <Select
              label={t('planMeal.generate.fieldSeason')}
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#10204a', color: '#fff' } } }}
            >
              <MenuItem value="auto">{t('planMeal.generate.seasonAuto')}</MenuItem>
              {SEASONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Temps de préparation --------------------------------------------*/}
        <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
          {t('planMeal.generate.sectionTime')}
        </Typography>
        <TextField
          size="small"
          type="number"
          label={t('planMeal.generate.fieldMaxPrep')}
          value={maxPrep}
          onChange={(e) => setMaxPrep(e.target.value)}
          inputProps={{ min: 0, step: 5 }}
          sx={{ ...fieldSx, width: 220 }}
        />
        <FormControlLabel
          sx={{ ...switchSx, display: 'block', mt: 0.5 }}
          control={
            <Switch
              checked={weekendDifferent}
              onChange={(e) => setWeekendDifferent(e.target.checked)}
            />
          }
          label={t('planMeal.generate.weekendDifferent')}
        />
        {weekendDifferent && (
          <TextField
            size="small"
            type="number"
            label={t('planMeal.generate.fieldMaxPrepWeekend')}
            value={maxPrepWeekend}
            onChange={(e) => setMaxPrepWeekend(e.target.value)}
            inputProps={{ min: 0, step: 5 }}
            sx={{ ...fieldSx, width: 260 }}
          />
        )}

        {/* Options ----------------------------------------------------------*/}
        <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
          {t('planMeal.generate.sectionOptions')}
        </Typography>
        <Stack>
          <FormControlLabel
            sx={switchSx}
            control={
              <Switch checked={noRepeat} onChange={(e) => setNoRepeat(e.target.checked)} />
            }
            label={t('planMeal.generate.optionNoRepeat')}
          />
          <FormControlLabel
            sx={switchSx}
            control={
              <Switch
                checked={multiCourse}
                onChange={(e) => setMultiCourse(e.target.checked)}
              />
            }
            label={t('planMeal.generate.optionMultiCourse')}
          />
          {multiCourse && (
            <Box sx={{ pl: 5, mb: 1 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, mb: 0.6 }}>
                {t('planMeal.generate.courseMealsLabel')}
              </Typography>
              <Stack direction="row" gap={0.5}>
                {MEALS.filter((m) => m !== 'Matin').map((meal) => (
                  <Chip
                    key={meal}
                    label={meal}
                    size="small"
                    onClick={() => toggleCourseMeal(meal)}
                    variant={courseMeals.includes(meal) ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 800,
                      color: '#fff',
                      bgcolor: courseMeals.includes(meal)
                        ? 'rgba(169,135,255,0.3)'
                        : 'transparent',
                      borderColor: 'rgba(255,255,255,0.25)',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
          <FormControlLabel
            sx={switchSx}
            control={
              <Switch checked={leftovers} onChange={(e) => setLeftovers(e.target.checked)} />
            }
            label={t('planMeal.generate.optionLeftovers')}
          />
        </Stack>

        {/* Mode -------------------------------------------------------------*/}
        <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
          {t('planMeal.generate.sectionMode')}
        </Typography>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value="complete"
            control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
            label={t('planMeal.generate.modeComplete')}
            sx={switchSx}
          />
          <FormControlLabel
            value="regenerate"
            control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
            label={t('planMeal.generate.modeRegenerate')}
            sx={switchSx}
          />
        </RadioGroup>

        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 800 }}
        >
          {t('planMeal.generate.btnCancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={16} sx={{ color: '#fff' }} />
            ) : (
              <AutoAwesomeIcon />
            )
          }
          sx={{
            bgcolor: '#1b75d0',
            textTransform: 'none',
            fontWeight: 900,
            borderRadius: 999,
            px: 2.4,
            '&:hover': { bgcolor: '#145da8' },
          }}
        >
          {loading
            ? t('planMeal.generate.generating')
            : t('planMeal.generate.btnGenerate')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
